import {type ReactNode} from 'react'
import {StyleSheet, Text, View} from 'react-native'
import Svg, {Circle} from 'react-native-svg'
import {Feather} from '@expo/vector-icons'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {
  directionTextStyle,
  type FlashBookDirection,
} from '#/lib/flashbookDirection'
import {type FlashBookReportModel} from '#/lib/flashbookReport'
import {
  REPORT_CARD_SIZE,
  reportCardFocus,
  reportCardGraded,
  type ReportCardStatKind,
  reportCardStats,
} from '#/lib/flashbookReportCard'
import {Logotype} from '#/view/icons/Logotype'
import {
  FlashBookPlayThemeProvider,
  useFlashBookPlayPalette,
  usePlayStackGap,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {
  formatDuration,
  useCopyStyle,
  useReportType,
  useReportVerdict,
} from '#/components/flashbooks/useReportChrome'
import {type FlashBookDesign} from '#/themes-flashbook/design'
import {type FlashBookThemeTokens} from '#/types/flashbook'

/**
 * The report, as the picture a learner posts.
 *
 * A poster and not a screenshot of the report: a card is read at a glance in
 * somebody else's timeline, often at thumbnail size, so it prints the four things
 * worth putting on a wall — the book, the verdict, the score, and what is still
 * open — and it prints them *large*. The choice of which numbers appear is the
 * model's (`#/lib/flashbookReportCard`), and the words are the report's own
 * (`useReportVerdict`), so the picture can disagree with the report in nothing but
 * size.
 *
 * It is drawn on the book's own paper in the book's own type, for the same reason
 * the report is: a book that ends in somebody else's application looking like a
 * screenshot of a different application would be the wrong last page.
 *
 * Two things are deliberately *not* shared with the report. The ring: the report's
 * is 128px inside a scrolling page, this one is a poster's, three times the
 * presence and read from across a room. And the type scale: every role is set
 * larger here, because the same 14px body that reads well on a page is unreadable
 * once a timeline has scaled the card down to a thumbnail.
 */
type Props = {
  bookTitle: string
  model: FlashBookReportModel
  themeTokens: FlashBookThemeTokens
  /** The book's design language, so the card is set like the book. */
  design?: FlashBookDesign
  /** The direction the book is written in — the title is the book's own writing. */
  direction?: FlashBookDirection
  /** Which read of the book this was, when the register knows. */
  readNumber?: number
  /** When the read ended, for the footer's date. */
  endedAt?: Date
}

export function FlashBookReportCard({
  themeTokens,
  design,
  direction,
  ...props
}: Props) {
  return (
    <FlashBookPlayThemeProvider
      tokens={themeTokens}
      design={design}
      direction={direction}>
      <CardBody {...props} tok={themeTokens} direction={direction} />
    </FlashBookPlayThemeProvider>
  )
}

function CardBody({
  bookTitle,
  model,
  readNumber,
  endedAt,
  tok,
  direction,
}: Omit<Props, 'themeTokens' | 'design'> & {tok: FlashBookThemeTokens}) {
  const {t: l, i18n} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  const gap = usePlayStackGap(14)
  const verdict = useReportVerdict(model)
  const graded = reportCardGraded(model)
  const stats = reportCardStats(model)
  const {rows: focus, more} = reportCardFocus(model)
  // The book's title is the book's writing, not the card's: it is set the way the
  // cover sets it even when everything around it is the reader's language.
  const titleDir = directionTextStyle(direction ?? 'ltr')

  return (
    <View
      testID="flashbook-report-card"
      style={[
        styles.card,
        {backgroundColor: tok.pageBg, borderColor: pal.paperHairline},
      ]}>
      {/* The theme's own furniture over the book's name: a rule in the accent the
          reader has been looking at for the whole book. */}
      <View style={styles.header}>
        <View style={[styles.rule, {backgroundColor: tok.accent}]} />
        <View style={[styles.ruleThin, {backgroundColor: pal.paperHairline}]} />
        <Text
          testID="flashbook-report-card-title"
          style={[type.caption, titleDir, styles.title, {color: pal.muted}]}
          numberOfLines={2}>
          {bookTitle}
        </Text>
      </View>

      {/* ── What it came to ─────────────────────────────────────────────── */}
      <View style={[styles.hero, {gap}]}>
        <ScoreRing
          pct={model.score.pct}
          graded={graded}
          accent={tok.accent}
          track={pal.paperTrack}
          ink={pal.text}
          muted={pal.muted}
        />
        <View style={styles.heroText}>
          <Text
            testID="flashbook-report-card-verdict"
            style={[type.display, copy.text, styles.verdict, {color: pal.text}]}
            numberOfLines={2}>
            {verdict.title}
          </Text>
          <Text
            style={[type.body, copy.text, styles.subtitle, {color: pal.body}]}
            numberOfLines={2}>
            {verdict.subtitle}
          </Text>
        </View>
      </View>

      {/* ── The numbers behind it ───────────────────────────────────────── */}
      <View style={[styles.tiles, copy.row]}>
        {stats.map(kind => (
          <Stat
            key={kind}
            kind={kind}
            model={model}
            accent={tok.accent}
            positive={pal.positive}
          />
        ))}
      </View>

      {/* ── What is still open, when anything is ────────────────────────── */}
      {focus.length > 0 && (
        <View style={[styles.focus, {borderColor: pal.paperHairline}]}>
          <Text
            style={[
              type.label,
              copy.text,
              styles.focusTitle,
              {color: pal.text},
            ]}>
            <Trans>Worth another look</Trans>
          </Text>
          {focus.map((entry, i) => (
            <View
              key={`${entry.pageIndex}-${entry.blockId}`}
              testID={`flashbook-report-card-focus--${i}`}
              style={[styles.focusRow, copy.row]}>
              <Text style={[type.caption, {color: pal.warning}]}>
                {entry.skipped ? '·' : '—'}
              </Text>
              <Text
                style={[
                  type.caption,
                  copy.text,
                  styles.focusPrompt,
                  {color: pal.body},
                ]}
                numberOfLines={1}>
                {entry.prompt || entry.label}
              </Text>
              <Text style={[type.caption, copy.text, {color: pal.muted}]}>
                {l`page ${entry.pageIndex + 1}`}
              </Text>
            </View>
          ))}
          {more > 0 && (
            <Text style={[type.caption, copy.text, {color: pal.muted}]}>
              <Plural value={more} one="and one more" other="and # more" />
            </Text>
          )}
        </View>
      )}

      {/* ── Whose read this was ─────────────────────────────────────────── */}
      <View style={[styles.footer, copy.row]}>
        <Text style={[type.caption, copy.text, {color: pal.muted}]}>
          {readNumber != null && readNumber > 0
            ? l`Read ${readNumber} · ${endedAt ? i18n.date(endedAt, {dateStyle: 'medium'}) : ''}`
            : endedAt
              ? i18n.date(endedAt, {dateStyle: 'medium'})
              : ''}
        </Text>
        <Logotype width={62} fill={pal.muted} />
      </View>
    </View>
  )
}

/**
 * The score, as a ring — the poster's, not the report's.
 *
 * Bigger and thicker than the report's, because it is the one thing a reader sees
 * before anything else in a timeline: at 148px and 13px of stroke it still reads as
 * a score once the feed has drawn the card at a third of its size. With nothing to
 * score the ring closes and the centre shows the book instead of claiming a
 * percentage no read earned — the same rule the report follows.
 */
function ScoreRing({
  pct,
  graded,
  accent,
  track,
  ink,
  muted,
}: {
  pct: number
  graded: boolean
  accent: string
  track: string
  ink: string
  muted: string
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const size = 148
  const stroke = 13
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const filled = graded
    ? (Math.min(100, Math.max(0, pct)) / 100) * circumference
    : circumference

  return (
    <View
      testID="flashbook-report-card-ring"
      style={[styles.ring, {width: size, height: size}]}
      accessibilityRole="image"
      accessibilityLabel={
        graded ? l`${pct}% of the points` : l`Reading finished`
      }
      accessibilityHint={l`Your score, as a ring`}>
      <Svg width={size} height={size}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={track}
          strokeWidth={stroke}
          fill="none"
        />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={accent}
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={`${filled} ${circumference}`}
          // Twelve o'clock, where a reader's eye already is.
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
      <View style={styles.ringCenter} pointerEvents="none">
        {graded ? (
          <>
            <Text
              testID="flashbook-report-card-score"
              style={[type.display, styles.score, {color: ink}]}>
              {pct}%
            </Text>
            <Text style={[type.caption, {color: muted}]}>
              <Trans>points</Trans>
            </Text>
          </>
        ) : (
          <Feather name="book-open" size={46} color={accent} />
        )}
      </View>
    </View>
  )
}

/**
 * One number, with the word for it.
 *
 * The words are the report's own — a card that said \"score\" where the report says
 * \"points\" would be the same feature two vocabularies, and the tile a learner taps
 * through to on their report has to be the tile they recognise on the picture.
 */
function Stat({
  kind,
  model,
  accent,
  positive,
}: {
  kind: ReportCardStatKind
  model: FlashBookReportModel
  accent: string
  positive: string
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()

  const {icon, value, label, tone} = ((): {
    icon: React.ComponentProps<typeof Feather>['name']
    value: string
    label: ReactNode
    tone?: string
  } => {
    switch (kind) {
      case 'points':
        return {
          icon: 'award',
          value: `${model.score.points}/${model.score.max}`,
          label: <Trans>Points</Trans>,
          tone: accent,
        }
      case 'correct':
        return {
          icon: 'check-circle',
          value: `${model.counts.correct}/${model.counts.exercises}`,
          label: <Trans>Correct</Trans>,
          tone: positive,
        }
      case 'readings':
        return {
          icon: 'headphones',
          value: `${model.counts.readingsHeard}/${model.counts.readings}`,
          label: <Trans>Readings</Trans>,
          tone: accent,
        }
      case 'pages':
        return {
          icon: 'book',
          value: `${model.pages.length}`,
          label: <Trans>Pages</Trans>,
          tone: accent,
        }
      default:
        return {
          icon: 'clock',
          value: formatDuration(model.time.totalMs),
          label: l`Time`,
          tone: accent,
        }
    }
  })()

  return (
    <View
      testID={`flashbook-report-card-stat--${kind}`}
      style={[
        styles.tile,
        {backgroundColor: pal.surfaceAlt, borderColor: pal.paperHairline},
      ]}>
      <Feather name={icon} size={17} color={tone ?? pal.muted} />
      <Text style={[type.title, styles.tileValue, {color: pal.text}]}>
        {value}
      </Text>
      <Text
        style={[type.caption, copy.text, styles.tileLabel, {color: pal.muted}]}>
        {label}
      </Text>
    </View>
  )
}

const styles = StyleSheet.create({
  card: {
    width: REPORT_CARD_SIZE.width,
    height: REPORT_CARD_SIZE.height,
    borderWidth: 1,
    padding: 30,
    justifyContent: 'space-between',
    overflow: 'hidden',
  },
  header: {gap: 7},
  rule: {height: 3, width: 56},
  ruleThin: {height: 1, width: '100%'},
  title: {fontSize: 17, fontWeight: '700', letterSpacing: 0.4, marginTop: 3},
  hero: {flexDirection: 'row', alignItems: 'center', gap: 20},
  heroText: {flex: 1, gap: 4},
  verdict: {fontSize: 30, fontWeight: '800', lineHeight: 34},
  subtitle: {fontSize: 15, lineHeight: 21},
  ring: {alignItems: 'center', justifyContent: 'center'},
  ringCenter: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  score: {fontSize: 40, fontWeight: '800'},
  tiles: {flexDirection: 'row', gap: 10},
  tile: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 10,
    alignItems: 'center',
    gap: 3,
  },
  tileValue: {fontSize: 21, fontWeight: '800'},
  tileLabel: {fontSize: 11},
  focus: {borderTopWidth: 1, paddingTop: 12, gap: 6},
  focusTitle: {fontSize: 14, fontWeight: '700', letterSpacing: 0.3},
  focusRow: {flexDirection: 'row', alignItems: 'center', gap: 8},
  focusPrompt: {flex: 1, fontSize: 13},
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
})
